import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, TrendingUp, Clock, AlertTriangle, ChevronLeft, ChevronRight, CheckCircle2, Camera, MessageSquareText, BarChart3 } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Badge, Button } from "@/components/ui-kit";

/**
 * Iter v31 — Karyawan self-history page (/karyawan/ringkasan).
 * Weekly / Monthly toggle. Summary cards + daily table + simple bar chart.
 */

const dayName = (d) => new Date(d + "T00:00").toLocaleDateString("id-ID", { weekday: "short" });
const fmtDate = (d) => new Date(d + "T00:00").toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
const fmtTime = (ts) => ts ? ts.slice(11, 16) : "—";

export default function KaryawanRingkasan() {
  const [mode, setMode] = useState("month"); // "week" | "month" | "range"
  const [cursorDate, setCursorDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Iter v34at — Custom date range filter for karyawan self-service summary.
  const [rangeFrom, setRangeFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 29);
    return d.toISOString().slice(0, 10);
  });
  const [rangeTo, setRangeTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = mode === "range"
        ? { mode, start_date: rangeFrom, end_date: rangeTo }
        : { mode, date: cursorDate };
      const r = await api.get("/attendance/me/history", { params });
      setData(r.data);
      setError(null);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.detail || "Halaman ini hanya untuk akun karyawan");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, cursorDate, rangeFrom, rangeTo]);

  const shiftPeriod = (dir) => {
    const d = new Date(cursorDate + "T00:00");
    if (mode === "week") d.setDate(d.getDate() + (dir * 7));
    else d.setMonth(d.getMonth() + dir);
    setCursorDate(d.toISOString().slice(0, 10));
  };

  const trend = data?.trend || [];
  const summary = data?.summary || {};
  const maxHours = useMemo(() => Math.max(8, ...trend.map((t) => (t.work_hours || 0) + (t.overtime_hours || 0))), [trend]);

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-5xl mx-auto p-4 md:p-6 lg:p-8 space-y-6" data-testid="karyawan-ringkasan-page">
      <PageHeader overline="Karyawan" title="Ringkasan Absensi Saya"
        description="Pantau riwayat absensi Anda per minggu atau bulan." />
      <div className="flex flex-wrap gap-2">
        <Link to="/karyawan/absen" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50"><Camera className="h-3.5 w-3.5" /> Absen Hari Ini</Link>
        <Link to="/karyawan/ringkasan" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-stone-900 text-white"><BarChart3 className="h-3.5 w-3.5" /> Ringkasan Saya</Link>
        <Link to="/karyawan/chat-hr" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50"><MessageSquareText className="h-3.5 w-3.5" /> Chat HR</Link>
      </div>

      {error && (
        <Card className="p-4 bg-amber-50 border-amber-200 text-sm text-amber-800 flex items-center gap-2" data-testid="ringkasan-error">
          <AlertTriangle className="h-4 w-4 text-amber-600" /> {error}
        </Card>
      )}

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex border border-stone-300" data-testid="ringkasan-mode-tabs">
            {[{ k: "week", l: "Mingguan" }, { k: "month", l: "Bulanan" }, { k: "range", l: "Rentang" }].map((t) => (
              <button key={t.k} onClick={() => setMode(t.k)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider ${mode === t.k ? "bg-terra-500 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}
                data-testid={`ringkasan-tab-${t.k}`}>
                {t.l}
              </button>
            ))}
          </div>
          {mode === "range" ? (
            <div className="flex items-center gap-2 flex-wrap" data-testid="ringkasan-range-controls">
              <label className="flex items-center gap-1 text-xs text-stone-600">
                Dari:
                <input type="date" value={rangeFrom} max={rangeTo}
                  onChange={(e) => setRangeFrom(e.target.value)}
                  className="border border-stone-300 px-2 py-1 text-xs focus:border-terra-500 outline-none"
                  data-testid="ringkasan-range-from" />
              </label>
              <label className="flex items-center gap-1 text-xs text-stone-600">
                Sampai:
                <input type="date" value={rangeTo} min={rangeFrom}
                  onChange={(e) => setRangeTo(e.target.value)}
                  className="border border-stone-300 px-2 py-1 text-xs focus:border-terra-500 outline-none"
                  data-testid="ringkasan-range-to" />
              </label>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Button variant="outline" onClick={() => shiftPeriod(-1)} data-testid="ringkasan-prev"><ChevronLeft className="h-3.5 w-3.5" /></Button>
              <Badge tone="stone"><Calendar className="h-3 w-3 inline" /> {data?.period_label || "—"}</Badge>
              <Button variant="outline" onClick={() => shiftPeriod(1)} data-testid="ringkasan-next"><ChevronRight className="h-3.5 w-3.5" /></Button>
            </div>
          )}
        </div>
        {mode === "range" && (
          <div className="text-[11px] text-stone-500" data-testid="ringkasan-range-label">
            Periode: <b>{data?.period_label || "—"}</b>
          </div>
        )}
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Hari Hadir</div>
          <div className="text-2xl font-display font-bold text-emerald-700 mt-1" data-testid="ringkasan-days-present">
            {summary.days_present || 0}<span className="text-sm text-stone-500"> / {summary.days_in_period || 0}</span>
          </div>
        </Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Total Jam Kerja</div>
          <div className="text-2xl font-display font-bold text-stone-900 mt-1" data-testid="ringkasan-total-hours">
            {(summary.total_work_hours || 0).toFixed(1)}<span className="text-sm text-stone-500"> jam</span>
          </div>
        </Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Total Lembur</div>
          <div className="text-2xl font-display font-bold text-amber-700 mt-1" data-testid="ringkasan-overtime">
            {(summary.total_overtime_hours || 0).toFixed(1)}<span className="text-sm text-stone-500"> jam</span>
          </div>
        </Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Keterlambatan</div>
          <div className="text-2xl font-display font-bold text-red-700 mt-1" data-testid="ringkasan-late">
            {summary.days_late || 0}<span className="text-sm text-stone-500"> hari ({(summary.late_percent || 0).toFixed(0)}%)</span>
          </div>
        </Card>
      </div>

      {/* Trend chart */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-3.5 w-3.5 text-terra-600" />
          <h3 className="font-display text-sm font-semibold text-stone-900">Tren Jam Kerja</h3>
          <span className="text-[11px] text-stone-500">(jam reguler + lembur, target {summary.expected_work_hours || 0}j)</span>
        </div>
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-1 items-end min-w-max" style={{ minHeight: "180px" }} data-testid="ringkasan-chart">
            {trend.map((t) => {
              const reg = t.work_hours - t.overtime_hours > 0 ? t.work_hours - t.overtime_hours : t.work_hours;
              const ot = t.overtime_hours || 0;
              const regH = (reg / maxHours) * 150;
              const otH = (ot / maxHours) * 150;
              return (
                <div key={t.date} className="flex flex-col items-center gap-1 min-w-[28px]" title={`${t.date} · ${t.work_hours.toFixed(1)}j${t.is_late ? " (telat)" : ""}`}>
                  <div className="text-[9px] font-mono text-stone-400">{t.work_hours > 0 ? t.work_hours.toFixed(1) : ""}</div>
                  <div className="flex flex-col-reverse w-5">
                    {regH > 0 && <div className={t.is_late ? "bg-red-400" : "bg-emerald-500"} style={{ height: `${regH}px` }} />}
                    {otH > 0 && <div className="bg-amber-500" style={{ height: `${otH}px` }} />}
                    {!t.present && <div className="bg-stone-100 border border-dashed border-stone-300" style={{ height: "4px" }} />}
                  </div>
                  <div className="text-[9px] text-stone-500">{mode === "week" ? dayName(t.date) : t.date.slice(8)}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-[11px] text-stone-600 mt-3">
          <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-emerald-500" /> Hadir tepat waktu</span>
          <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-red-400" /> Hadir terlambat</span>
          <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-amber-500" /> Lembur</span>
          <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-stone-100 border border-dashed border-stone-300" /> Tidak hadir</span>
        </div>
      </Card>

      {/* Daily detail */}
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-3 py-3">Tanggal</th>
              <th className="text-center px-3 py-3">Jam Masuk</th>
              <th className="text-center px-3 py-3">Jam Keluar</th>
              <th className="text-right px-3 py-3">Jam Kerja</th>
              <th className="text-right px-3 py-3">Lembur</th>
              <th className="text-center px-3 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading && <tr><td colSpan={6} className="px-3 py-12 text-center text-xs text-stone-400">Memuat…</td></tr>}
            {!loading && trend.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-12 text-center text-xs text-stone-400">Belum ada data.</td></tr>
            )}
            {!loading && trend.map((t) => (
              <tr key={t.date} className={`hover:bg-stone-50/40 ${!t.present && "opacity-50"}`} data-testid={`ringkasan-row-${t.date}`}>
                <td className="px-3 py-2.5 font-mono text-xs">{fmtDate(t.date)} · <span className="text-stone-400">{dayName(t.date)}</span></td>
                <td className="px-3 py-2.5 text-center font-mono text-xs">
                  {t.present && t.is_late
                    ? <span className="text-red-600">{(() => { const r = (data.rows || []).find((x) => x.date === t.date); return fmtTime(r?.clock_in_local_ts || r?.clock_in_ts); })()}</span>
                    : (() => { const r = (data.rows || []).find((x) => x.date === t.date); return fmtTime(r?.clock_in_local_ts || r?.clock_in_ts); })()}
                </td>
                <td className="px-3 py-2.5 text-center font-mono text-xs">
                  {(() => { const r = (data.rows || []).find((x) => x.date === t.date); return fmtTime(r?.clock_out_local_ts || r?.clock_out_ts); })()}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{t.work_hours ? `${t.work_hours.toFixed(2)}j` : "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{t.overtime_hours ? `${t.overtime_hours.toFixed(2)}j` : "—"}</td>
                <td className="px-3 py-2.5 text-center">
                  {!t.present ? <Badge tone="stone">tidak hadir</Badge>
                    : t.status === "completed" ? <Badge tone="emerald"><CheckCircle2 className="h-3 w-3 inline" /> selesai</Badge>
                    : t.status === "pending_approval" ? <Badge tone="amber">menunggu</Badge>
                    : t.status === "rejected" ? <Badge tone="red">ditolak</Badge>
                    : <Badge tone="stone">{t.status}</Badge>}
                  {t.is_late && t.present && <Badge tone="red" className="ml-1"><AlertTriangle className="h-3 w-3 inline" /> telat</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-3 bg-stone-50 text-[11px] text-stone-600">
        <Clock className="h-3 w-3 inline text-stone-500" /> Jam kerja standar: <b>{summary.expected_work_hours || 0} jam</b> ({data?.standard_work_hours || 0}j × {summary.days_in_period || 0} hari). Pemenuhan: <b className="text-emerald-700">{(summary.fulfilment_percent || 0).toFixed(0)}%</b>.
      </Card>
    </div>
    </div>
  );
}
